import type {
  ShoppingList,
  ShoppingListItem,
  ShoppingReminder,
  ShoppingReminderRepeat,
} from '@recipes/contracts';
import { SHOPPING_REMINDER_REPEATS } from '@recipes/contracts';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { Bell, BellOff, WifiOff } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { queryKeys } from '@/lib/query-keys';
import { currentFormatLocale } from '@/lib/i18n';
import { deviceTimeZone, proposal, toLocalInput } from './reminder-format';

const EVERYBODY = '__everybody__';

/**
 * "Remind me" for a list or one of its items: when, how often, and whom – a member of the
 * household the list is shared with, or everybody who can see it. Saved on the server, which
 * sends it as a notification (push where this device subscribed); needs a connection.
 */
export function ReminderDialog({
  open,
  onOpenChange,
  list,
  item,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list: ShoppingList;
  /** The item the reminder is for; absent = the list itself. */
  item?: ShoppingListItem | null;
  onChanged: (reminder: ShoppingReminder | null) => void;
}) {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const ids = { when: useId(), repeat: useId(), who: useId() };
  const current = item ? (item.reminder ?? null) : (list.reminder ?? null);
  const [when, setWhen] = useState('');
  const [repeat, setRepeat] = useState<ShoppingReminderRepeat>('NONE');
  const [who, setWho] = useState(EVERYBODY);
  const [saving, setSaving] = useState(false);
  const members = useQuery({
    queryKey: queryKeys.households.detail(list.householdId ?? ''),
    queryFn: () => api.households.get(list.householdId!),
    enabled: open && Boolean(list.householdId) && online,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!open) return;
    setWhen(current ? toLocalInput(new Date(current.remindAt)) : proposal());
    setRepeat(current?.repeat ?? 'NONE');
    setWho(current?.assignee?.id ?? EVERYBODY);
  }, [open, current]);

  const chosen = when ? new Date(when) : null;
  const inPast =
    chosen !== null &&
    repeat === 'NONE' &&
    chosen.getTime() <= Date.now() &&
    !Number.isNaN(chosen.getTime());
  const valid = chosen !== null && !Number.isNaN(chosen.getTime()) && !inPast;

  const save = async () => {
    if (!valid || !chosen) return;
    setSaving(true);
    try {
      const body = {
        remindAt: chosen.toISOString(),
        repeat,
        timeZone: deviceTimeZone(),
        assigneeId: who === EVERYBODY ? null : who,
      };
      const saved = item
        ? await api.shopping.setItemReminder(list.id, item.id, body)
        : await api.shopping.setReminder(list.id, body);
      onChanged(saved);
      toast.success(t('shopping.reminder.saved'));
      onOpenChange(false);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      if (item) await api.shopping.removeItemReminder(list.id, item.id);
      else await api.shopping.removeReminder(list.id);
      onChanged(null);
      toast.success(t('shopping.reminder.removed'));
      onOpenChange(false);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="size-5" aria-hidden />
            {item
              ? t('shopping.reminder.titleItem', { name: item.name })
              : t('shopping.reminder.titleList', { name: list.name })}
          </DialogTitle>
          <DialogDescription>{t('shopping.reminder.description')}</DialogDescription>
        </DialogHeader>
        {!online && (
          <Alert variant="warning">
            <WifiOff className="size-4" aria-hidden /> {t('shopping.reminder.offline')}
          </Alert>
        )}
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div>
            <Label htmlFor={ids.when}>{t('shopping.reminder.when')}</Label>
            <Input
              id={ids.when}
              type="datetime-local"
              className="mt-1"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              aria-invalid={inPast || undefined}
              data-testid="reminder-when"
            />
            {inPast && (
              <p role="alert" className="mt-1 text-sm text-destructive">
                {t('shopping.reminder.past')}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor={ids.repeat}>{t('shopping.reminder.repeatLabel')}</Label>
            <Select value={repeat} onValueChange={(v) => setRepeat(v as ShoppingReminderRepeat)}>
              <SelectTrigger id={ids.repeat} className="mt-1" data-testid="reminder-repeat">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHOPPING_REMINDER_REPEATS.map((value) => (
                  <SelectItem key={value} value={value} data-testid={`reminder-repeat-${value}`}>
                    {t(`shopping.reminder.repeat.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {repeat !== 'NONE' && chosen && !Number.isNaN(chosen.getTime()) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t(`shopping.reminder.repeatHint.${repeat}`, {
                  weekday: chosen.toLocaleDateString(currentFormatLocale(), { weekday: 'long' }),
                  day: chosen.getDate(),
                  time: chosen.toLocaleTimeString(currentFormatLocale(), {
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
                })}
              </p>
            )}
          </div>
          {list.householdId && (
            <div>
              <Label htmlFor={ids.who}>{t('shopping.reminder.who')}</Label>
              <Select value={who} onValueChange={setWho}>
                <SelectTrigger id={ids.who} className="mt-1" data-testid="reminder-who">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EVERYBODY}>{t('shopping.reminder.everybody')}</SelectItem>
                  {members.data?.members.map((member) => (
                    <SelectItem
                      key={member.user.id}
                      value={member.user.id}
                      data-testid="reminder-member"
                    >
                      {member.user.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            {current ? (
              <Button
                type="button"
                variant="ghost"
                disabled={!online || saving}
                onClick={() => void remove()}
                data-testid="reminder-remove"
              >
                <BellOff aria-hidden /> {t('shopping.reminder.remove')}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                loading={saving}
                disabled={!online || !valid}
                data-testid="reminder-save"
              >
                {t('common.save')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
